import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const csvHeaders = [
  { label: 'Date', key: 'inv_date' },
  { label: 'Item Name', key: 'item_name' },
  { label: 'Sold Quantity', key: 'sold' },
];

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const ProductWiseSales = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemName, setItemName] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchSalesData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://serverdemo.ccsoftwares.com/sales/productWise');
        const data = response.data || [];
        data.sort((a, b) => {
          const dateA = new Date(a.inv_date);
          const dateB = new Date(b.inv_date);
          return dateB - dateA;
        });
        setSalesData(data);
      } catch (err) {
        console.error('Failed to fetch product-wise sales', err);
        setError('Unable to load product sales data at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [itemName, fromDate, toDate]);

  const filteredData = useMemo(() => {
    if (!salesData.length) return [];

    const normalizedItem = itemName.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return salesData.filter((record) => {
      const matchesItem = normalizedItem
        ? record.item_name?.toLowerCase().includes(normalizedItem)
        : true;

      if (!matchesItem) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      const recordDate = record.inv_date ? new Date(record.inv_date) : null;
      if (!recordDate) {
        return false;
      }

      const afterFrom = from ? recordDate >= from : true;
      const beforeTo = to ? recordDate <= to : true;
      return afterFrom && beforeTo;
    });
  }, [salesData, itemName, fromDate, toDate]);

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredData;
    }
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const todaysSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredData.reduce((accumulator, record) => {
      const recordDate = record.inv_date ? new Date(record.inv_date) : null;
      if (!recordDate) return accumulator;
      recordDate.setHours(0, 0, 0, 0);
      if (recordDate.getTime() === today.getTime()) {
        return accumulator + Number(record.sold || 0);
      }
      return accumulator;
    }, 0);
  }, [filteredData]);

  const selectedPeriodQuantity = useMemo(
    () =>
      filteredData.reduce(
        (accumulator, record) => accumulator + Number(record.sold || 0),
        0
      ),
    [filteredData]
  );

  const uniqueProductsCount = useMemo(() => {
    const unique = new Set(filteredData.map((record) => record.item_name));
    return unique.size;
  }, [filteredData]);

  const csvData = useMemo(() => filteredData, [filteredData]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const TOP_PRODUCTS_LIMIT = 20;

  const productTotals = useMemo(() => {
    const totals = new Map();
    filteredData.forEach(({ item_name, sold }) => {
      const name = String(item_name ?? '').trim();
      if (!name) return;
      totals.set(name, (totals.get(name) || 0) + Number(sold || 0));
    });

    return Array.from(totals.entries())
      .map(([product, quantity]) => ({ product, quantity }))
      .sort((a, b) => b.quantity - a.quantity);
  }, [filteredData]);

  const productChartData = useMemo(() => {
    if (productTotals.length <= TOP_PRODUCTS_LIMIT) {
      return productTotals;
    }

    const top = productTotals.slice(0, TOP_PRODUCTS_LIMIT);
    const othersQuantity = productTotals
      .slice(TOP_PRODUCTS_LIMIT)
      .reduce((sum, row) => sum + Number(row.quantity || 0), 0);

    return othersQuantity > 0
      ? [...top, { product: 'Others', quantity: othersQuantity }]
      : top;
  }, [productTotals]);

  const chartHeight = useMemo(() => {
    const rowHeight = 32;
    return Math.max(360, productChartData.length * rowHeight);
  }, [productChartData.length]);

  const truncateLabel = (value) => {
    const label = String(value ?? '');
    return label.length > 22 ? `${label.slice(0, 22)}...` : label;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Product Wise Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Evaluate item performance with date and product filters.
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename="product-wise-sales.csv"
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredData.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Today&apos;s Sold Quantity
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {todaysSales.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Sold Quantity
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {selectedPeriodQuantity.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Unique Products (Filtered)
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {uniqueProductsCount.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search Item Name"
              placeholder="Start typing a product name"
              fullWidth
              value={itemName}
              onChange={(event) => setItemName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Sold Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No product sales match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow key={`${row.inv_date}-${row.item_name}-${index}`}>
                    <TableCell>{row.inv_date}</TableCell>
                    <TableCell>{row.item_name}</TableCell>
                    <TableCell>{Number(row.sold || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          Top Products (Selected Period)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {productTotals.length === 0
            ? 'No products to display for the selected filters.'
            : productTotals.length > TOP_PRODUCTS_LIMIT
              ? `Showing top ${TOP_PRODUCTS_LIMIT} products (remaining grouped as Others).`
              : `Showing all products (${productTotals.length}).`}
        </Typography>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ height: 420, overflowY: 'auto' }}>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={productChartData}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 140, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  type="category"
                  dataKey="product"
                  width={180}
                  tickFormatter={truncateLabel}
                />
                <Tooltip
                  formatter={(value) => Number(value || 0).toLocaleString()}
                  labelFormatter={(label) => `Product: ${label}`}
                />
                <Bar dataKey="quantity" name="Sold Quantity" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ProductWiseSales;
